import { useEffect, useState } from "react";
import {
  Activity,
  Bike,
  CheckCircle,
  Dumbbell,
  Flame,
  Footprints,
  Plus,
  Waves,
  X,
  type LucideIcon,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";
import manualExerciseService from "../services/ManualExerciseService";
import { appendExerciseLogEntry } from "../utils/exerciseLogHistory";
import {
  DEFAULT_EXERCISE_TYPE,
  DEFAULT_INTENSITY,
  EXERCISE_TYPES,
  INTENSITY_LEVELS,
  INTENSITY_THEME,
  exerciseTheme,
  getTypeMeta,
} from "../utils/manualExerciseUi";
import {
  gluceraFormControl,
  gluceraMacroLabel,
  measureCardTitle,
  measureCardValueSubhead,
  measureEyebrow,
  measurePrimaryButtonLabel,
  measureSectionTitle,
} from "../utils/gluceraTypography";

export type ManualExerciseLogSheetProps = {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
};

const ManualExerciseLogSheet = ({
  open,
  onClose,
  onSaved,
}: ManualExerciseLogSheetProps) => {
  const [selectedType, setSelectedType] = useState(DEFAULT_EXERCISE_TYPE);
  const [exerciseName, setExerciseName] = useState("");
  const [durationHours, setDurationHours] = useState("");
  const [durationMinutes, setDurationMinutes] = useState("");
  const [intensity, setIntensity] = useState<"low" | "medium" | "high">(DEFAULT_INTENSITY);
  const [manualCalories, setManualCalories] = useState("");
  const [isLogging, setIsLogging] = useState(false);

  const resetForm = () => {
    setSelectedType(DEFAULT_EXERCISE_TYPE);
    setExerciseName("");
    setDurationHours("");
    setDurationMinutes("");
    setIntensity(DEFAULT_INTENSITY);
    setManualCalories("");
  };

  useEffect(() => {
    if (!open) return;
    resetForm();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isLogging) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, isLogging]);

  const typeObj = getTypeMeta(selectedType);
  const isOtherType = selectedType === "other";
  const intensityObj =
    INTENSITY_LEVELS.find((i) => i.id === intensity) ?? INTENSITY_LEVELS[1];
  const totalDurationMin =
    (Number(durationHours) || 0) * 60 + (Number(durationMinutes) || 0);
  const estimatedCal =
    totalDurationMin > 0
      ? Math.round(totalDurationMin * typeObj.metPerMin * intensityObj.multiplier)
      : 0;

  const inputClass = cn(
    gluceraFormControl,
    "rounded-xl bg-background py-2.5",
    exerciseTheme.inputFocus
  );

  const handleLogExercise = async () => {
    if (isLogging) return;

    const hours = Number(durationHours) || 0;
    const mins = Number(durationMinutes) || 0;
    if (hours < 0 || mins < 0 || mins >= 60 || totalDurationMin <= 0) {
      toast.error("Please enter a valid duration (hours and 0–59 minutes).");
      return;
    }

    const name = isOtherType
      ? exerciseName.trim()
      : typeObj.label;
    if (isOtherType && !name) {
      toast.error("Please enter an exercise name.");
      return;
    }
    const calNum = manualCalories.trim()
      ? Number(manualCalories)
      : estimatedCal;
    if (!calNum || calNum <= 0) {
      toast.error("Please enter calories or a valid duration.");
      return;
    }

    setIsLogging(true);
    try {
      await manualExerciseService.logManualExercise({
        exercise_type: selectedType,
        exercise_name: name,
        duration_hours: hours,
        duration_min: mins,
        intensity,
        calories_kcal: calNum,
      });

      appendExerciseLogEntry({
        name,
        duration: String(totalDurationMin),
        calories: String(calNum),
        type: selectedType,
        intensity,
      });

      onSaved?.();
      onClose();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Could not log exercise. Try again.";
      toast.error(message);
    } finally {
      setIsLogging(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col justify-end"
      role="dialog"
      aria-modal="true"
      aria-labelledby="exercise-log-sheet-title"
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={() => !isLogging && onClose()}
        aria-hidden
      />

      <div className="relative z-10 mx-auto w-full max-w-[400px] animate-in slide-in-from-bottom-4 duration-200">
        <div className="max-h-[min(92dvh,_820px)] overflow-y-auto scrollbar-hide rounded-t-3xl border border-border bg-background shadow-2xl">
          <div className="flex justify-center pb-2 pt-3">
            <div className="h-1 w-11 rounded-full bg-muted-foreground/25" aria-hidden />
          </div>

          <div className="flex items-center justify-between px-5 pb-1 pt-0.5">
            <p className={measureEyebrow}>Activity · Log exercise</p>
            <button
              type="button"
              onClick={onClose}
              disabled={isLogging}
              className="rounded-full p-1 text-muted-foreground hover:bg-muted disabled:opacity-50"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-4 px-5 pb-[max(1.25rem,env(safe-area-inset-bottom,1rem))] pt-2">
            <div className="flex items-center gap-3">
              <span
                className={cn(
                  "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl",
                  exerciseTheme.iconWrap
                )}
              >
                <Dumbbell className={cn("h-5 w-5", exerciseTheme.icon)} strokeWidth={2} />
              </span>
              <h2 id="exercise-log-sheet-title" className={measureSectionTitle}>
                Log manual exercise
              </h2>
            </div>

            <div>
              <p className={cn("mb-2", measureEyebrow)}>Exercise type</p>
              <div className="grid grid-cols-3 gap-2">
                {EXERCISE_TYPES.map((t) => {
                  const Icon = t.icon as LucideIcon;
                  const active = selectedType === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => {
                        setSelectedType(t.id);
                        if (t.id !== "other") setExerciseName("");
                      }}
                      className={cn(
                        "flex flex-col items-center gap-1.5 rounded-xl border py-2.5 transition-all active:scale-[0.98]",
                        active
                          ? exerciseTheme.tileActive
                          : "border-border/70 bg-background hover:bg-muted/30"
                      )}
                    >
                      <div
                        className={cn(
                          "flex h-9 w-9 items-center justify-center rounded-full",
                          active ? exerciseTheme.tileIconActive : "bg-muted"
                        )}
                      >
                        <Icon
                          className={cn(
                            "h-4 w-4",
                            active ? "text-white" : "text-muted-foreground"
                          )}
                          strokeWidth={active ? 2.25 : 2}
                        />
                      </div>
                      <span
                        className={cn(
                          active ? exerciseTheme.tileTextActive : gluceraMacroLabel,
                          active && "font-bold"
                        )}
                      >
                        {t.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {isOtherType ? (
              <div>
                <label className={cn("mb-1.5 block", measureEyebrow)}>Exercise name</label>
                <input
                  type="text"
                  value={exerciseName}
                  onChange={(e) => setExerciseName(e.target.value)}
                  placeholder="e.g. Yoga Session"
                  className={inputClass}
                />
              </div>
            ) : null}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={cn("mb-1.5 block", measureEyebrow)}>Hours</label>
                <input
                  type="number"
                  inputMode="numeric"
                  min="0"
                  value={durationHours}
                  onChange={(e) => setDurationHours(e.target.value)}
                  placeholder="0"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={cn("mb-1.5 block", measureEyebrow)}>Minutes</label>
                <input
                  type="number"
                  inputMode="numeric"
                  min="0"
                  max="59"
                  value={durationMinutes}
                  onChange={(e) => setDurationMinutes(e.target.value)}
                  placeholder="30"
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <p className={cn("mb-2", measureEyebrow)}>Intensity</p>
              <div className="grid grid-cols-3 gap-2">
                {INTENSITY_LEVELS.map((lvl) => {
                  const active = intensity === lvl.id;
                  const tone = INTENSITY_THEME[lvl.id];
                  return (
                    <button
                      key={lvl.id}
                      type="button"
                      onClick={() => setIntensity(lvl.id)}
                      className={cn(
                        "rounded-xl border py-2 transition-all active:scale-[0.98]",
                        measureCardTitle,
                        active
                          ? cn(tone.active, "shadow-sm")
                          : "border-border/70 bg-background text-muted-foreground hover:bg-muted/30"
                      )}
                    >
                      {lvl.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 items-end gap-3">
              <div>
                <label className={cn("mb-1.5 block", measureEyebrow)}>Calories (kcal)</label>
                <input
                  type="number"
                  inputMode="numeric"
                  min="0"
                  value={manualCalories}
                  onChange={(e) => setManualCalories(e.target.value)}
                  placeholder={estimatedCal > 0 ? String(estimatedCal) : "Auto"}
                  className={inputClass}
                />
              </div>
              {estimatedCal > 0 ? (
                <div
                  className={cn(
                    "flex items-center gap-2 rounded-xl border px-3 py-2.5",
                    exerciseTheme.estimateBox
                  )}
                >
                  <Flame className={cn("h-4 w-4 shrink-0", exerciseTheme.icon)} />
                  <div>
                    <p className={measureEyebrow}>Estimated</p>
                    <p className={cn(measureCardValueSubhead, exerciseTheme.estimateText)}>
                      {estimatedCal} kcal
                    </p>
                  </div>
                </div>
              ) : null}
            </div>

            <Button
              type="button"
              onClick={() => void handleLogExercise()}
              disabled={isLogging}
              className={cn(
                "h-11 w-full rounded-xl bg-primary text-primary-foreground shadow-md",
                "hover:bg-primary/90 active:scale-[0.98]",
                "disabled:pointer-events-none disabled:opacity-60",
                measurePrimaryButtonLabel
              )}
            >
              <CheckCircle className="mr-2 h-4 w-4" strokeWidth={2.25} />
              {isLogging ? "Logging…" : "Log Exercise"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManualExerciseLogSheet;
